import { ApiError } from './api.js';
import { isNonStaffArea } from './nonStaffAreas.js';

// ── The last thing between a refused save and a silent screen ────────────────
//
// 2026-08-27 cost three phone calls, and all three were one bug wearing
// different clothes: the server refused clearly, and the screen said nothing.
// An empty מנהל פרויקט dropdown behind a 403. A drawing rejected by a stale
// copy of the filename pattern. A department that would not stick because a
// PATCH demanded a field the screen never sends. In every case the code knew
// exactly what was wrong, and the person in front of it was left guessing —
// and then guessing costs an hour and a call.
//
// A sweep found 75 mutations out of 230 with no error handling of any kind:
// no onError, no isError rendered, no try around mutateAsync. Fixing those one
// at a time would be 75 chances to be inconsistent, and mutation 231 would be
// silent again the day after.
//
// So the default flips. Anything that fails to save now says so, everywhere,
// without being asked — and a screen that wants to present its own error still
// does, because this only speaks when nothing else has.

// ── מה נכשל, לא רק שמשהו נכשל ────────────────────────────────────────────────
//
// The banner headline was hardcoded ״הפעולה לא נשמרה״ and `reportReadError`
// pushes messages that begin ״לא ניתן לטעון את הנתונים״ — so a list that failed
// to LOAD was announced to the user as a save that did not happen. Two
// contradictory sentences in one box, and the person is left deciding which
// half to believe. Caught on 2026-09-01 by looking at a screenshot of the real
// quotes page, which is the only way this kind of thing is ever caught.
export type SaveErrorKind = 'SAVE' | 'READ';
export type SaveError = { id: number; message: string; at: number; kind: SaveErrorKind };

type Listener = (errors: SaveError[]) => void;

let seq = 0;
let live: SaveError[] = [];
const listeners = new Set<Listener>();

const emit = (): void => { for (const l of listeners) l(live); };

export function subscribeSaveErrors(l: Listener): () => void {
  listeners.add(l);
  l(live);
  return () => { listeners.delete(l); };
}

export function dismissSaveError(id: number): void {
  live = live.filter((e) => e.id !== id);
  emit();
}

/**
 * Report a failed save.
 *
 * Deduped by message: a screen that retries three times has one problem, not
 * three, and three identical banners is how somebody learns to close them
 * without reading.
 */
export function reportSaveError(error: unknown): void {
  const message = messageOf(error);
  if (!message) return;
  if (live.some((e) => e.message === message)) return;
  // A handful at most. Past that the screen is the problem, not the list.
  live = [...live, { id: ++seq, message, at: Date.now(), kind: 'SAVE' as const }].slice(-3);
  emit();
}

/**
 * Report a failed READ.
 *
 * Separate entry point from the save above only for the wording: a person
 * reading an empty table needs to be told the list did not load, not that
 * something did not save. Everything else — the dedupe, the cap, the banner —
 * is the same machinery.
 */
export function reportReadError(error: unknown): void {
  // ── סירוב הרשאה אינו תקלה ─────────────────────────────────
  //
  // צולם ב-2026-09-05: מהנדס פתח את מסך הניהול וקיבל שתי תשובות
  // סותרות באותו מסך: בגוף המסך משפט נכון — "למשתמש שלך אין הרשאת
  // צפייה בעלויות… מנהל המערכת יכול להוסיף אותה" — ובפינה באנר
  // אדום "לא ניתן לטעון את הנתונים". הראשון מסביר, השני מבהיל.
  //
  // הכלל ב-SPEC כתוב מפורשת: סירוב הרשאה אינו נראה כשגיאת רשת
  // שכדאי לנסות שוב. אותה החלטה שכבר נתקבלה על 401 במסך צוות.
  //
  // רק בקריאה. סירוב על **שמירה** חייב להיאמר — זו עצם הסיבה
  // שהקובץ הזה נכתב, אחרי שלוש שיחות טלפון ב-2026-08-27.
  if (error instanceof ApiError && error.status === 403) return;
  // ── וגם 401, ובקריאה בלבד ───────────────────────────────────────────
  //
  // נמדד 07/09/2026 בסבב הקיוסקים: ‎/scan/issue‎ בלי סשן הציג **שתי**
  // תשובות סותרות, בדיוק כמו מסך הניהול שבגללו נכתבה השורה שמעל: פאנל
  // ענבר נכון — „העמדה אינה מחוברת“ — ומעליו באנר אדום „לא ניתן לטעון
  // את הנתונים · יש להתחבר“. הראשון מסביר, השני מבהיל.
  //
  // ‎messageOf‎ מחזיר 401 באזורי הקיוסק בכוונה, כי עובד שהתג שלו פג באמצע
  // משמרת חייב לקבל סיבה. אבל זה נכון ל**שמירה** — ללחיצה שלא עשתה כלום.
  // קריאה שנכשלת ב-401 היא מצב המנוחה של קיוסק שטרם נכנס אליו איש, וכל
  // קיוסק כאן כבר כותב את זה בעצמו במילים שלו.
  if (error instanceof ApiError && error.status === 401) return;
  // ── וגם 404 של מעטפת הצוות באזור שאינו של הצוות ─────────────────────────
  //
  // אלי, 16/09/2026 23:33, מצילום בטלפון: „יש תקלה כלשהי שקופצת בכניסה
  // לפורטל." שוחזר מול הייצור — לקוח פותח `portal.edal.co.il/portal`, הטופס
  // ריק, ובפינה באנר אדום „לא ניתן לטעון את הנתונים · שגיאת רשת".
  //
  // מה שקורה: `App.tsx` מרנדר את מעטפת הצוות מעל **כל** הנתיבים, ולכן
  // `AuthContext` יורה `/api/auth/me` גם בפורטל. ה-vhost של הפורטל מעביר
  // `/api/portal/*` בלבד — ובצדק, ללקוח אין ולא יהיה סשן משרדי — ולכן
  // התשובה היא 404 מ-nginx. `AuthContext` תופס 401 ומחזיר null, אבל 404
  // נזרק הלאה ונוחת כאן.
  //
  // זו אותה משפחה בדיוק שבגללה נכתב `nonStaffAreas.ts` ב-01/09: קוד אחד
  // מניח „מישהו כבר טיפל בזה" בשעה שבאזור הזה איש לא טיפל. ההפך מהמקרה ההוא:
  // שם 401 נבלע ולא היה צריך, כאן 404 נאמר ולא היה צריך. אותו גבול, אותה
  // רשימה, קורא שלישי.
  //
  // רק בקריאה, ורק שם: 404 על **שמירה**, ו-404 במסך משרדי, נשארים נאמרים.
  if (error instanceof ApiError && error.status === 404 && isNonStaffArea(window.location.pathname)) return;
  const message = messageOf(error);
  if (!message) return;
  // The prefix moves into the HEADLINE, so the box does not say the same thing
  // twice and the message stays the server's own sentence.
  if (live.some((e) => e.message === message && e.kind === 'READ')) return;
  live = [...live, { id: ++seq, message, at: Date.now(), kind: 'READ' as const }].slice(-3);
  emit();
}

export function messageOf(error: unknown): string | null {
  if (error instanceof ApiError) {
    // 401 has its own path — ApiAuthGuard sends the person to the login screen,
    // and a banner about it on the way out is noise.
    //
    // But ONLY on a staff screen. The guard deliberately does nothing on /scan,
    // /field and /portal, whose users have no staff session to expire, so
    // swallowing the 401 there left a worker whose badge token had run out with
    // a button that did nothing and no reason given. The refusal has a sentence
    // written for exactly that person; show it.
    if (error.status === 401 && !isNonStaffArea(window.location.pathname)) return null;
    // סקר העמדה 11/10 #3: גוף 5xx באנגלית („Internal server error", מפרוקסי או
    // מ-nginx) הגיע כמו שהוא לבאנר. משפט שאומר מה לעשות במקומו.
    if (error.status >= 500 && !/[֐-׿Ѐ-ӿ]/.test(error.userMessage)) {
      return 'השרת לא ענה — נסה שוב בעוד רגע. אם זה חוזר — קרא למנהל.';
    }
    return error.userMessage;
  }
  if (error instanceof Error && error.message) return error.message;
  return 'הפעולה נכשלה. נסה שוב.';
}
